/**
 * The health probes (PLAN §10.2's status path, §11.1): what `GET /health`
 * answers on the cloud API and on a node. Unauthenticated, cheap, and NOT in
 * the route table — they are not tenant data, they answer before a caller
 * could have a token (the connectivity panel probes them before and during
 * sign-in), and the cloud serves `/health` on its own unauthenticated API
 * Gateway route (`infrastructure/api-http.tf`) in front of the router.
 *
 *     cloud:  { service: 'cloud-api', stage, version, time }
 *     node:   { service: 'state-service', status, stage, version, time,
 *               tenantId, siteId, nodeId, label, storeId, schemaVersion,
 *               cloudLink, outboxDepth }
 *
 * `version` is what is running: the cloud API's bundle hash (set by OpenTofu
 * from the Lambda zip), a node's package hash (D15; `ACP_VERSION`, `dev`
 * until packaging lands). `time` is the server's clock (ISO), so a reader can
 * show drift.
 *
 * `cloudLink` is the node's own view of its site link to the cloud (PLAN
 * §10.2's "Node→cloud" column): `not-configured` until the site link exists
 * (phase 4), then `connected` / `disconnected`. `outboxDepth` is how many
 * applied patches wait in the node's outbox for that link.
 *
 * The producers answer these strict schemas (their tests parse what they
 * answer). The READERS — the CLI's `acp status`, the SPA's probes — parse
 * `anyHealthSchema`, built from the `.loose()` variants: they talk to servers
 * of other versions (PLAN §4.3), and a newer server adding a field must not
 * read as unreachable.
 */
import { z } from 'zod';

const isoTime = z.iso.datetime({ offset: true });

export const cloudHealthSchema = z
  .object({
    service: z.literal('cloud-api'),
    stage: z.string().min(1),
    version: z.string().min(1),
    time: isoTime,
  })
  .strict();

export const cloudLinkStateSchema = z.enum(['not-configured', 'connected', 'disconnected']);

export const nodeHealthSchema = z
  .object({
    service: z.literal('state-service'),
    /** Liveness, kept from the first shape of this endpoint. */
    status: z.literal('ok'),
    /** The cloud stage this node belongs to (`ACP_STAGE`), or `local` for a node with none configured. */
    stage: z.string().min(1),
    version: z.string().min(1),
    time: isoTime,
    tenantId: z.string().min(1),
    siteId: z.string().min(1),
    nodeId: z.string().min(1),
    /** What operators call this node (`ACP_NODE_LABEL`, default the node id). */
    label: z.string().min(1),
    storeId: z.string().min(1),
    schemaVersion: z.number().int(),
    cloudLink: cloudLinkStateSchema,
    outboxDepth: z.number().int().min(0),
  })
  .strict();

/**
 * What a reader accepts: either service, unknown extra fields kept, and the
 * closed values opened up — a newer node reporting `status: 'degraded'` or a
 * cloud link state this reader does not know must read as REACHABLE, not as
 * "not an ACP health report" (which would fail a working primary over). A
 * link state it does not know is, as far as it can tell, not connected.
 */
export const anyHealthSchema = z.discriminatedUnion('service', [
  cloudHealthSchema.loose(),
  nodeHealthSchema.extend({ status: z.string().min(1), cloudLink: cloudLinkStateSchema.catch('disconnected') }).loose(),
]);

export type CloudHealth = z.infer<typeof cloudHealthSchema>;
export type NodeHealth = z.infer<typeof nodeHealthSchema>;
export type AnyHealth = z.infer<typeof anyHealthSchema>;
export type CloudLinkState = z.infer<typeof cloudLinkStateSchema>;

/** Where every host answers its health probe, relative to its API base URL. */
export const HEALTH_PATH = '/health';
